import React from 'react';
import {
    Box,
    Card,
    CardHeader,
    Divider,
    Fade,
    IconButton,
    SvgIcon,
    Table,
    TableBody,
    makeStyles,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Edit2 } from 'react-feather';
import { CapitalizeNames } from 'src/utils/capitalize';
import type { TServiceClientVehicle } from 'src/interfaces/serviceClient';
import { formatDate } from 'src/utils/formatDate';
import { TextRow } from 'src/components/Lead/Information/components/TextRow';
import type { TValue } from 'src/components/Lead/Information/components/types';
import numeral from 'numeral';

const useStyles = makeStyles(() => ({
    paper: {
        overflow: 'scroll',
    },
    margin: {
        marginBottom: '1em',
    },
    editButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
}));

type VehicleCardProps = {
    vehicle: TServiceClientVehicle;
    editPath?: string;
};

export const VehicleCard = ({ vehicle, editPath }: VehicleCardProps) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const values: (TValue & { dataTestId?: string })[] = [
        {
            header: t('serviceClient.Make'),
            text: vehicle.make?.name ? CapitalizeNames(vehicle.make.name) : '—',
            dataTestId: 'vehicle-card-row-make',
        },
        {
            header: t('serviceClient.Model'),
            text: vehicle.model ?? '—',
            dataTestId: 'vehicle-card-row-model',
        },
        {
            header: t('serviceClient.ModelType'),
            text: vehicle.modelType ?? '—',
            dataTestId: 'vehicle-card-row-modelType',
        },
        {
            header: t('serviceClient.Year'),
            text: vehicle.year ?? '—',
            dataTestId: 'vehicle-card-row-year',
        },
        {
            header: t('serviceClient.Kilometers'),
            text:
                vehicle.kilometers != null
                    ? numeral(vehicle.kilometers).format('0,0') + ' km'
                    : '—',
            dataTestId: 'vehicle-card-row-kilometers',
        },
        {
            header: t('serviceClient.Vin'),
            text: vehicle.vin?.toUpperCase() ?? '—',
            dataTestId: 'vehicle-card-row-vin',
        },
        {
            header: t('serviceClients.createdAt'),
            text: formatDate(vehicle.createdAt),
            dataTestId: 'vehicle-card-row-createdAt',
        },
    ];

    return (
        <Fade in={true} timeout={600}>
            <Card
                className={classes.paper}
                data-testid="vehicle-card"
                elevation={0}
            >
                <Box className={classes.editButton}>
                    <CardHeader title={String(t('Vehicles.Info'))} />
                    {editPath && (
                        <IconButton
                            component={RouterLink}
                            to={editPath}
                            size="small"
                            aria-label={t('serviceClient.UpdateVehicle')}
                            data-testid="vehicle-card-edit-button"
                        >
                            <SvgIcon fontSize="small">
                                <Edit2 />
                            </SvgIcon>
                        </IconButton>
                    )}
                </Box>

                <Divider />
                <Table className={classes.margin}>
                    <TableBody>
                        {values.map((item, index) => (
                            <TextRow
                                key={index}
                                item={item}
                                loading={false}
                                data-testid={item.dataTestId}
                            />
                        ))}
                    </TableBody>
                </Table>
            </Card>
        </Fade>
    );
};
